import { navigationResource } from '../Navigation.i18n.ts';
import { languageCodes } from '../../../i18n/language.ts';
import appPackage from '../../../../package.json';
import { getTotalSpacingOfBoxModel, getTotalWidthOfBoxModel } from '../../../utils/dom/getBoxModel.ts';
import { NavigationNavLinksTranslations } from '../Navigation.i18n.ts';

/**
 * Keys for caching navigation-related measurements in localStorage.
 */
const CACHE_KEYS = {
    navLinkWidth: "navLinksWidth",
    requiredWidthForFullNavbar: "requiredWidthForFullNavbar"
} as const;

/**
 * Interface for caching the navigation link width.
 */
interface NavLinkWidthCache {
    version: string;
    navLinkWidth: number;
}

/**
 * Interface for caching the required width for the full navbar.
 */
interface RequiredWidthForFullNavbarCache {
    version: string;
    requiredWidthForFullNavbar: number;
}

/**
 * Parameters for calculating the required width for the full navbar.
 */
interface RequiredWidthForFullNavbarParams {
    navbarBrandElement: HTMLAnchorElement;
    navbarNavElement: HTMLDivElement;
    languageDropdownSelectElement: HTMLSelectElement;
    navLinksAmount: number;
    navLinkWidth: number;
}

/**
 * Calculate the largest width required for navigation anchors based on their translated text.
 * @throws {Error} If the canvas context cannot be created.
 * @returns {number} The largest width in pixels between all navigation anchors across all available languages.
 */
export const calculateLargestNavigationAnchorWidth = (): number => {
    // Create a canvas context to measure text width.
    // This is to simulate the rendering of text in the navigation anchors.
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

    // Throw an error if the context could not be created.
    // This could happen for example when we call this function in a testing or non-browser environment.
    if (!context) {
        throw new Error('Could not create the canvas context to calculate text width for navigation anchors. This could happen in non-browser environments.');
    }

    const computedStyles: CSSStyleDeclaration = getComputedStyle(document.documentElement);

    // Get the font family using the root variable set in index.scss
    const fontFamilyRootVar: string = '--font-family';
    const fontFamily: string = computedStyles.getPropertyValue(fontFamilyRootVar).trim();

    if (!fontFamily) {
        console.error(`Font family for navigation anchors is not defined in root variable ${fontFamilyRootVar}.`);
    }

    // Get the padding using the root variable set in Navigation.module.scss
    const paddingRootVar: string = '--navbar-nav-links-padding';
    const padding: string = computedStyles.getPropertyValue(paddingRootVar).trim();

    if (!padding) {
        console.error(`Padding for navigation anchors is not defined in root variable ${paddingRootVar}.`);
    }

    // Set the context font to match the navigation anchor styles.
    // This is done to simulate the exact text width as it would appear in the navigation.
    context.font = `${computedStyles.fontSize} ${fontFamily}`;

    // Get the total padding (left + right) for the anchors.
    const widthPadding: number = parseInt(padding, 10) * 2;

    // Get the keys from the English navigation resource as a baseline.
    // ! Note that all languages should have the same keys for the translation, so it doesn't matter which language we use to get the keys, as long as the language exists.
    // ! Because of this, we just grab the English navigation resource keys.
    const navItems: string[] = Object.keys(navigationResource.en.navLinks);
    
    let maxWidth: number = 0;
    
    // Iterate over all available languages and navigation items to find the maximum width of the translated text.
    for (const languageCode of languageCodes) {
        for (const key of navItems) {
            const translation: string = navigationResource[languageCode].navLinks[key as keyof NavigationNavLinksTranslations];
            const textWidth: number = context.measureText(translation).width;

            const totalWidth: number = Math.ceil(textWidth + widthPadding);

            if (totalWidth > maxWidth) {
                maxWidth = totalWidth;
            }
        }
    }

    return maxWidth;
};

/**
 * Get the cached navigation link width from localStorage or calculate it if not cached.
 * @returns {number} The width of the navigation link in pixels.
 */
export const getNavLinkWidth = (): number => {
    const cacheKey = CACHE_KEYS.navLinkWidth;
    
    // Try to retrieve the cached navigation link width from localStorage.
    // We use try/except here to avoid breaking the application if localStorage is not available.
    // E.g., in private browsing modes, or if the user / browser settings disable localStorage.
    try {
        const navLinkWidthCache: string | null = localStorage.getItem(cacheKey);
        if (navLinkWidthCache) {
            const data: NavLinkWidthCache = JSON.parse(navLinkWidthCache);
            if (data.version === appPackage.version) {
                return data.navLinkWidth;
            }
        }
    } catch (error) {
        console.error('Error retrieving cached navigation link width from localStorage. Calculating new value.', error);
    }

    const navLinkWidth: number = calculateLargestNavigationAnchorWidth();
    const navLinkWidthCacheData: NavLinkWidthCache = {
        version: appPackage.version,
        navLinkWidth: navLinkWidth
    };

    // Cache the calculated navigation link width in localStorage.
    // We use try/except here to avoid breaking the application if localStorage is not available.
    // E.g., in private browsing modes, or if the user / browser settings disable localStorage.
    try {
        localStorage.setItem(cacheKey, JSON.stringify(navLinkWidthCacheData));
    } catch (error) {
        console.error('Error caching navigation link width in localStorage.', error);
    }

    return navLinkWidth;
};

/**
 * Calculate the required width for displaying the full navbar without collapsing.
 * @param {RequiredWidthForFullNavbarParams} params - The parameters required for the calculation.
 * @returns {number} The required width for the full navbar in pixels.
 */
const calculateRequiredWidthForFullNavbar = (params: RequiredWidthForFullNavbarParams): number => {
    const {
        navbarBrandElement,
        navbarNavElement,
        languageDropdownSelectElement,
        navLinksAmount,
        navLinkWidth
    } = params;

    let totalWidth: number = 0;

    totalWidth += getTotalWidthOfBoxModel(navbarBrandElement);

    totalWidth += getTotalSpacingOfBoxModel(navbarNavElement);

    totalWidth += getTotalWidthOfBoxModel(languageDropdownSelectElement);

    totalWidth += navLinkWidth * navLinksAmount;

    // Add the width of all the gaps between the nav links
    const navLinksGap: string = getComputedStyle(document.documentElement).getPropertyValue('--navbar-nav-links-gap').trim();
    const fontSize: string = getComputedStyle(document.documentElement).fontSize;
    const navLinksGapInPx: number = parseFloat(navLinksGap) * parseFloat(fontSize);
    const totalNavLinksGapInPx: number = navLinksGapInPx * navLinksAmount;
    totalWidth += totalNavLinksGapInPx;

    // Add 1 extra gap width to account for the gap between the language dropdown and the right end of the navbar
    totalWidth += navLinksGapInPx;


    // Add the horizontal padding of the navbar
    const horizontalNavbarPadding: string = getComputedStyle(document.documentElement).getPropertyValue('--navbar-padding-horizontal').trim();
    const horizontalNavbarPaddingInPx: number = parseFloat(horizontalNavbarPadding) * 2;
    totalWidth += horizontalNavbarPaddingInPx;

    return totalWidth;
}

/**
 * Get the required width for displaying the full navbar, either from cache or by calculating it.
 * @param {RequiredWidthForFullNavbarParams} params The parameters required for the calculation.
 * @returns {number} The required width for the full navbar in pixels.
 */
export const getRequiredWidthForFullNavbar = (params: RequiredWidthForFullNavbarParams): number => {
    const cacheKey: string = CACHE_KEYS.requiredWidthForFullNavbar;

    // Try to retrieve the cached value from localStorage
    // We use try/except here to avoid breaking the application if localStorage is not available.
    // E.g., in private browsing modes, or if the user / browser settings disable localStorage.
    try {
        const requiredWidthForFullNavbarCache: string | null = localStorage.getItem(cacheKey);
        if (requiredWidthForFullNavbarCache) {
            const parsedValue = JSON.parse(requiredWidthForFullNavbarCache) as RequiredWidthForFullNavbarCache;
            if (parsedValue.version === appPackage.version) {
                return parsedValue.requiredWidthForFullNavbar;
            }
        }
    } catch (error) {
        console.error('Error retrieving cached required width for full navbar from localStorage. Calculating new value.', error);
    }

    const requiredWidthForFullNavbar: number = calculateRequiredWidthForFullNavbar(params);

    // Cache the calculated value in localStorage
    // We use try/except here to avoid breaking the application if localStorage is not available.
    // E.g., in private browsing modes, or if the user / browser settings disable localStorage.
    try {
        const cacheData: RequiredWidthForFullNavbarCache = {
            version: appPackage.version,
            requiredWidthForFullNavbar: requiredWidthForFullNavbar
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
        console.error('Error caching required width for full navbar in localStorage.', error);
    }
    return requiredWidthForFullNavbar;
}